import { useState, useEffect, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RefreshCw, Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const API = `${window.location.origin}/api/budget`;

const UNIT_OPTIONS = ["SF", "LF", "Each", "Each Wall", "LS", "Unit", "Stalls", "Notches", "Seats", "Intakes", "Per Bag"];
const RESPONSIBILITY_OPTIONS = ["CFCI", "OFCI"];  // '' handled via NO_RESP sentinel
const NEW_GROUP_SENTINEL = "__NEW__";
const NO_GROUP_SENTINEL = "__NO_GROUP__";
const NO_RESP_SENTINEL = "__NO_RESP__";

export default function AdminPricing() {
  const [scopes, setScopes] = useState([]);
  const [activeScope, setActiveScope] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  // Add-line-item modal state (also reused for Edit)
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [editId, setEditId] = useState(null); // when set → modal is in Edit mode for this template.id
  const emptyForm = { description: "", unit: "Each", base_price: "", group: NO_GROUP_SENTINEL, newGroup: "", responsibility: NO_RESP_SENTINEL };
  const [addForm, setAddForm] = useState(emptyForm);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/scopes`)
      .then(r => {
        // Sort: active first, then alphabetical
        const sorted = [...r.data].sort((a, b) => {
          if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
          return (a.name || "").localeCompare(b.name || "");
        });
        setScopes(sorted);
        // Default to first ACTIVE scope; fall back to first inactive if none active
        const firstActive = sorted.find(s => s.is_active) || sorted[0];
        if (firstActive) {
          setActiveScope(firstActive.id);
          setTemplates(firstActive.line_item_templates || []);
        }
      })
      .catch(() => toast.error("Failed to load scopes"))
      .finally(() => setLoading(false));
  }, []);

  const selectScope = (scopeId) => {
    const scope = scopes.find(s => s.id === scopeId);
    if (scope) {
      setActiveScope(scopeId);
      setTemplates(scope.line_item_templates || []);
    }
  };

  const updateLocalTemplate = (idx, field, val) => {
    setTemplates(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: field === "base_price" ? parseFloat(val) || 0 : val };
      return updated;
    });
  };

  const saveTemplate = async (template) => {
    setSaving(template.id);
    try {
      await axios.put(`${API}/admin/scopes/${activeScope}/templates/${template.id}`, {
        base_price: template.base_price,
        unit: template.unit,
      });
      // Update scopes cache
      setScopes(prev => prev.map(s => {
        if (s.id === activeScope) {
          return { ...s, line_item_templates: templates };
        }
        return s;
      }));
      toast.success("Price updated");
    } catch {
      toast.error("Failed to update price");
    } finally {
      setSaving(null);
    }
  };

  const activeScopeObj = scopes.find(s => s.id === activeScope);
  const activeScopeName = activeScopeObj?.name || "";

  // Unique existing groups for this scope (for the "Add" form group dropdown).
  const existingGroups = Array.from(
    new Set(templates.map(t => (t.group || "").trim()).filter(g => g))
  );

  const openAddModal = () => {
    setEditId(null);
    setAddForm({ ...emptyForm, group: existingGroups[0] || NO_GROUP_SENTINEL });
    setAddOpen(true);
  };

  const openEditModal = (t) => {
    setEditId(t.id);
    setAddForm({
      description: t.description || "",
      unit: t.unit || "Each",
      base_price: t.base_price == null ? "" : String(t.base_price),
      group: t.group ? t.group : NO_GROUP_SENTINEL,
      newGroup: "",
      responsibility: t.responsibility ? t.responsibility : NO_RESP_SENTINEL,
    });
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!addForm.description.trim()) {
      toast.error("Description is required");
      return;
    }
    if (addForm.group === NEW_GROUP_SENTINEL && !addForm.newGroup.trim()) {
      toast.error("Enter a new group name");
      return;
    }
    const resolvedGroup = addForm.group === NEW_GROUP_SENTINEL
      ? addForm.newGroup.trim()
      : (addForm.group === NO_GROUP_SENTINEL ? "" : addForm.group);
    const resolvedResponsibility = addForm.responsibility === NO_RESP_SENTINEL
      ? ""
      : addForm.responsibility;
    setAddBusy(true);
    try {
      const payload = {
        description: addForm.description.trim(),
        unit: addForm.unit,
        base_price: parseFloat(addForm.base_price) || 0,
        group: resolvedGroup,
        responsibility: resolvedResponsibility,
      };
      let data;
      if (editId) {
        ({ data } = await axios.put(`${API}/admin/scopes/${activeScope}/templates/${editId}`, payload));
      } else {
        ({ data } = await axios.post(`${API}/admin/scopes/${activeScope}/templates`, payload));
      }
      setTemplates(data.templates);
      setScopes(prev => prev.map(s => s.id === activeScope
        ? { ...s, line_item_templates: data.templates }
        : s));
      toast.success(editId ? "Line item updated" : "Line item added");
      setAddOpen(false);
      setEditId(null);
      setAddForm(emptyForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (editId ? "Failed to update line item" : "Failed to add line item"));
    } finally {
      setAddBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setDeleteBusy(true);
    try {
      const { data } = await axios.delete(`${API}/admin/scopes/${activeScope}/templates/${deleteId}`);
      setTemplates(data.templates);
      setScopes(prev => prev.map(s => s.id === activeScope
        ? { ...s, line_item_templates: data.templates }
        : s));
      toast.success("Line item deleted");
      setDeleteId(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete line item");
    } finally {
      setDeleteBusy(false);
    }
  };

  // Group templates by `group` field preserving original idx for save/edit handlers.
  const groupedTemplates = (() => {
    const indexed = templates.map((t, idx) => ({ ...t, _idx: idx }));
    const hasGroups = indexed.some(t => t.group && String(t.group).trim());
    if (!hasGroups) return null;
    const groups = [];
    const map = new Map();
    indexed.forEach(t => {
      const g = (t.group || "").trim() || "Other";
      if (!map.has(g)) {
        const entry = { name: g, items: [] };
        map.set(g, entry);
        groups.push(entry);
      }
      map.get(g).items.push(t);
    });
    return groups;
  })();

  const renderTemplateRow = (t, idx) => (
    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`pricing-row-${idx}`}>
      <td className="px-5 py-3 text-slate-700">
        {t.description}
        {t.is_misc && <span className="ml-2 text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">MISC</span>}
        {t.responsibility && <span className="ml-2 text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{t.responsibility}</span>}
      </td>
      <td className="px-4 py-3">
        <Select
          value={t.unit}
          onValueChange={val => updateLocalTemplate(idx, "unit", val)}
        >
          <SelectTrigger className="h-7 w-24 text-xs rounded-sm border-slate-200" data-testid={`unit-select-${idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 justify-end">
          <span className="text-slate-400 text-sm">$</span>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={t.base_price}
            onChange={e => updateLocalTemplate(idx, "base_price", e.target.value)}
            className="h-7 w-24 text-right rounded-sm text-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
            data-testid={`price-input-${idx}`}
          />
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            onClick={() => saveTemplate(t)}
            disabled={saving === t.id}
            className="h-7 rounded-sm text-xs gap-1 px-2"
            data-testid={`save-price-${idx}`}
          >
            {saving === t.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openEditModal(t)}
            className="h-7 border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm text-xs px-2"
            data-testid={`edit-price-${idx}`}
            title="Edit line item"
          >
            <Pencil className="w-3 h-3" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDeleteId(t.id)}
            className="h-7 border-red-200 text-red-600 hover:bg-red-50 rounded-sm text-xs px-2"
            data-testid={`delete-price-${idx}`}
            title="Delete line item"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </td>
    </tr>
  );

  if (loading) return <div className="p-8 text-slate-400">Loading...</div>;

  return (
    <div className="p-4" data-testid="admin-pricing">
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Pricing Management
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Update base unit prices for each scope module</p>
      </div>

      <div className="flex gap-6">
        {/* Scope selector */}
        <div className="w-64 shrink-0">
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-3 py-2.5 border-b border-slate-200">
              <span className="text-xs text-slate-500 font-medium">All Scopes</span>
            </div>
            {scopes.map(scope => (
              <button
                key={scope.id}
                onClick={() => selectScope(scope.id)}
                className={`flex items-center gap-2 w-full px-3 py-2.5 text-left text-sm transition-colors border-l-2 ${
                  activeScope === scope.id
                    ? "border-[#007BFF] bg-[#007BFF]/10 text-[#007BFF] font-medium"
                    : "border-transparent text-slate-700 dark:text-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`pricing-scope-${scope.id}`}
              >
                <span className="flex-1 truncate">{scope.name}</span>
                {!scope.is_active && (
                  <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded shrink-0">Inactive</span>
                )}
              </button>
            ))}
            {scopes.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-400 text-center">No scopes yet. Create one in Scope Modules.</div>
            )}
          </div>
        </div>

        {/* Price table */}
        <div className="flex-1">
          {activeScope ? (
            <div className="bg-white border border-slate-200 rounded-sm" data-testid="pricing-table">
              <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  {activeScopeName} — Line Item Pricing
                  {activeScopeObj && !activeScopeObj.is_active && (
                    <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" data-testid="pricing-inactive-badge">
                      Inactive — activate in Scope Modules once ready
                    </span>
                  )}
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{templates.length} items</span>
                  <Button
                    size="sm"
                    onClick={openAddModal}
                    className="h-7 rounded-sm text-xs gap-1 px-2"
                    data-testid="add-line-item-btn"
                  >
                    <Plus className="w-3 h-3" /> Add Line Item
                  </Button>
                </div>
              </div>
              {templates.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-5 py-2.5 text-xs text-slate-500 font-medium">Item Description</th>
                        <th className="text-left px-4 py-2.5 text-xs text-slate-500 font-medium w-28">Unit</th>
                        <th className="text-right px-4 py-2.5 text-xs text-slate-500 font-medium w-36">Base Price</th>
                        <th className="px-4 py-2.5 w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedTemplates ? (
                        groupedTemplates.map(grp => (
                          <Fragment key={grp.name}>
                            <tr className="bg-[#161616]/5 border-b border-[#161616]/20">
                              <td colSpan={4} className="px-5 py-2 text-xs font-semibold text-[#161616]">
                                {grp.name}
                              </td>
                            </tr>
                            {grp.items.map(t => renderTemplateRow(t, t._idx))}
                          </Fragment>
                        ))
                      ) : (
                        templates.map((t, idx) => renderTemplateRow(t, idx))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-12 text-center text-sm text-slate-400">
                  No line items yet. Click <span className="font-medium text-slate-600">+ Add Line Item</span> above to create the first one.
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm py-16 text-center">
              <p className="text-slate-400 text-sm">Select an active scope to manage pricing</p>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Line Item modal */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setEditId(null); }}>
        <DialogContent className="max-w-md" data-testid="add-line-item-modal">
          <DialogHeader>
            <DialogTitle className="text-base">{editId ? "Edit Line Item" : "Add Line Item"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {editId ? "Update" : "Add a new"} line item {editId ? "in" : "to"} <span className="font-medium text-slate-700">{activeScopeName}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Description <span className="text-red-500">*</span></Label>
              <Input
                value={addForm.description}
                onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                placeholder="e.g. Aluminum Corner Guard, 8'-0&quot;"
                className="h-9 text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
                data-testid="add-line-description"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Unit</Label>
                <Select value={addForm.unit} onValueChange={v => setAddForm(p => ({ ...p, unit: v }))}>
                  <SelectTrigger className="h-9 text-sm rounded-sm border-slate-200" data-testid="add-line-unit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Base Price</Label>
                <div className="flex items-center gap-1">
                  <span className="text-slate-400 text-sm">$</span>
                  <Input
                    type="number" min="0" step="0.01"
                    value={addForm.base_price}
                    onChange={e => setAddForm(p => ({ ...p, base_price: e.target.value }))}
                    placeholder="0.00"
                    className="h-9 text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
                    data-testid="add-line-price"
                  />
                </div>
              </div>
            </div>
            {existingGroups.length > 0 && (
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Group</Label>
                <Select
                  value={addForm.group || NO_GROUP_SENTINEL}
                  onValueChange={v => setAddForm(p => ({ ...p, group: v }))}
                >
                  <SelectTrigger className="h-9 text-sm rounded-sm border-slate-200" data-testid="add-line-group">
                    <SelectValue placeholder="No group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_GROUP_SENTINEL}>No group</SelectItem>
                    {existingGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    <SelectItem value={NEW_GROUP_SENTINEL}>+ New group…</SelectItem>
                  </SelectContent>
                </Select>
                {addForm.group === NEW_GROUP_SENTINEL && (
                  <Input
                    value={addForm.newGroup}
                    onChange={e => setAddForm(p => ({ ...p, newGroup: e.target.value }))}
                    placeholder="New group name"
                    className="h-9 mt-2 text-sm rounded-sm border-slate-200 focus:border-[#161616] focus:ring-1 focus:ring-[#161616]"
                    data-testid="add-line-new-group"
                  />
                )}
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Responsibility (optional)</Label>
              <Select
                value={addForm.responsibility || NO_RESP_SENTINEL}
                onValueChange={v => setAddForm(p => ({ ...p, responsibility: v }))}
              >
                <SelectTrigger className="h-9 text-sm rounded-sm border-slate-200" data-testid="add-line-responsibility">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_RESP_SENTINEL}>—</SelectItem>
                  {RESPONSIBILITY_OPTIONS.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => { setAddOpen(false); setEditId(null); }} className="rounded-sm border-slate-300 text-slate-600 text-xs" data-testid="add-line-cancel-btn">
              Cancel
            </Button>
            <Button size="sm" onClick={submitAdd} disabled={addBusy} className="rounded-sm text-xs" data-testid="add-line-submit-btn">
              {addBusy ? (editId ? "Saving…" : "Adding…") : (editId ? "Save Changes" : "Add Line Item")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-line-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this line item?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the template from the master scope. New budgets will no longer include it.
              Existing budgets that already used it keep their original snapshot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-line-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-line-confirm-btn"
            >
              {deleteBusy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
